import React, { useState, useEffect, useRef } from 'react';
import { useAptMetrixContext } from '../context/AptMetrixContext';

const RegionDropdownMetrix = ({ 
    selectedRegion,
    selectedCity,
    onRegionChange,
    onCityChange,
    siDoData,
    siGunGuData 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const previousActiveRegionRef = useRef(null);
    const isUserInteractionRef = useRef(false);  // 사용자 인터랙션 추적
    
    // Context에서 상태 가져오기
    const {
        selectedRegions,
        setSelectedRegions,
        selectedCities,
        setSelectedCities,
        activeRegion,
        setActiveRegion,
        selectedRegionNames,
        setSelectedRegionNames
    } = useAptMetrixContext();
    
    // 각 시/도별 시/군/구 데이터 캐시
    const [siGunGuDataCache, setSiGunGuDataCache] = useState({});
    
    // 최대 선택 가능한 시/도 수
    const MAX_REGION_SELECTION = 3;
    // 최대 선택 가능한 시/군/구 수
    const MAX_CITY_SELECTION = 3;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // activeRegion이 변경될 때마다 해당 지역의 시/군/구 데이터를 요청
    useEffect(() => {
        // 이전 activeRegion과 현재 activeRegion이 다를 때만 실행
        if (activeRegion && previousActiveRegionRef.current !== activeRegion) {
            // 사용자 인터랙션인지 확인
            const isUserInitiated = isUserInteractionRef.current;
            
            onRegionChange({ 
                target: { value: activeRegion },
                autoSearch: false,
                userInitiated: isUserInitiated  // 사용자 인터랙션 여부 전달
            });
            
            previousActiveRegionRef.current = activeRegion;
            
            // 플래그 리셋
            isUserInteractionRef.current = false;
        }
    }, [activeRegion, onRegionChange]);

    // 시/군/구 데이터가 업데이트되면 캐시에 저장
    useEffect(() => {
        if (activeRegion && siGunGuData && siGunGuData.length > 0) {
            setSiGunGuDataCache(prev => {
                // 이미 같은 데이터가 캐시에 있다면 업데이트하지 않음
                if (prev[activeRegion] === siGunGuData) {
                    return prev;
                }
                return {
                    ...prev,
                    [activeRegion]: siGunGuData
                };
            });
        }
    }, [activeRegion, siGunGuData]);

    // 시/도 선택 핸들러
    const handleRegionSelect = (region) => {
        // 사용자 인터랙션 플래그 설정
        isUserInteractionRef.current = true;
        
        // 이미 선택된 시/도인지 확인
        const isAlreadySelected = selectedRegions.includes(region.cortarNo);
        
        if (isAlreadySelected) {
            // 이미 선택된 시/도면 제거하고 해당 시/도의 시/군/구도 제거
            setSelectedRegions(selectedRegions.filter(r => r !== region.cortarNo));
            setSelectedRegionNames(prev => {
                const updated = {...prev};
                delete updated[region.cortarNo];
                return updated;
            });
            
            // 해당 시/도에 속한 시/군/구 모두 제거
            setSelectedCities(selectedCities.filter(city => 
                !siGunGuDataCache[region.cortarNo]?.some(regionCity => 
                    regionCity.cortarNo === city.cortarNo
                )
            ));
            
            // 선택 취소시 activeRegion을 null로 설정
            setActiveRegion(null);
        } else {
            // 최대 선택 개수 확인
            if (selectedRegions.length >= MAX_REGION_SELECTION) {
                alert(`최대 ${MAX_REGION_SELECTION}개의 시/도만 선택할 수 있습니다.`);
                return;
            }
            
            // 선택되지 않은 시/도면 추가
            setSelectedRegions([...selectedRegions, region.cortarNo]);
            setSelectedRegionNames(prev => ({
                ...prev,
                [region.cortarNo]: region.cortarName
            }));
            
            // 활성 시/도 설정
            setActiveRegion(region.cortarNo);
        }
    };

    // 시/군/구 체크박스 변경 핸들러
    const handleCityCheckboxChange = (city) => {
        // 이미 선택된 도시인지 확인
        const isAlreadySelected = selectedCities.some(
            selectedCity => selectedCity.cortarNo === city.cortarNo
        );
        
        if (isAlreadySelected) {
            // 이미 선택된 도시라면 제거
            setSelectedCities(
                selectedCities.filter(item => item.cortarNo !== city.cortarNo)
            );
        } else {
            // 선택 제한 확인
            if (selectedCities.length >= MAX_CITY_SELECTION) {
                return;
            }
            
            // 선택되지 않은 도시라면 추가
            // 소속 시/도 정보도 함께 저장
            setSelectedCities([...selectedCities, {
                ...city,
                parentRegion: activeRegion,
                parentRegionName: selectedRegionNames[activeRegion]
            }]);
        }
    };

    // 모든 시/군/구 선택 해제 핸들러
    const handleDeselectAllCities = () => {
        setSelectedCities([]);
    };

    // 검색 실행 핸들러
    const handleSearch = () => {
        // 시/도가 선택되어 있고, 시/군/구가 하나 이상 선택되어 있는 경우
        if (selectedRegions.length > 0 && selectedCities.length > 0) {
            // 선택된 시/군/구 정보를 상위 컴포넌트로 전달
            onCityChange({ 
                target: { value: selectedCities.map(city => city.cortarNo) },
                selectedCities: selectedCities,
                regionNames: selectedRegionNames,
                multipleRegions: true,
                searchClicked: true, // 검색 버튼이 클릭되었음을 명시
                autoSearch: true
            });
            setIsOpen(false);
        } else if (selectedRegions.length > 0 && selectedCities.length === 0) {
            // 시/군/구가 선택되지 않은 경우 알림
            alert("최소 1개의 시/군/구를 선택해주세요");
        } else {
            // 시/도가 선택되지 않은 경우
            alert("지역을 선택해주세요");
        }
    };

    // 검색 버튼 활성화 여부
    const isSearchButtonDisabled = selectedRegions.length === 0 || selectedCities.length === 0;

    // 현재 활성화된 시/도의 시/군/구 데이터 가져오기
    const activeRegionSiGunGuData = activeRegion ? (siGunGuDataCache[activeRegion] || siGunGuData) : [];

    return (
        <div className="relative w-full md:w-auto" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`px-2 py-1 rounded-full font-bold text-green-600 bg-gray-100 focus:outline-none cursor-pointer text-xs md:text-sm hover:bg-green-600 hover:text-white min-w-[200px] w-[200px] 
                    ${selectedRegions.length > 0 ? 'bg-green-600 text-white font-medium' : ''}`}
            >
                {selectedRegions.length === 0 ? "지역을 선택하세요." : 
                    `${selectedRegions.length}개 지역, ${selectedCities.length}개 시/군/구`
                }
            </button>

            {isOpen && (
                <div className="absolute z-[9999] mt-2 bg-white rounded-lg shadow-lg border border-gray-200 w-full md:w-[500px] left-0">
                    <div className="flex flex-col">
                        <div className="flex flex-row h-[47vh]">
                            {/* 시/도 목록 */}
                            <div className="w-1/2 overflow-y-auto border-r border-gray-200">
                                <div className="bg-gray-50 p-2 border-b border-gray-200 sticky top-0">
                                    <h3 className="text-sm font-medium text-gray-700">시/도 선택</h3>
                                </div>
                                {siDoData?.map((region) => (
                                    <div
                                        key={region.cortarNo}
                                        className={`p-2 cursor-pointer hover:bg-gray-100 
                                            ${activeRegion === region.cortarNo ? 'bg-gray-100' : ''} 
                                            ${selectedRegions.includes(region.cortarNo) ? 'bg-green-50 border-l-4 border-green-500' : ''} text-xs md:text-sm`}
                                        onClick={() => handleRegionSelect(region)}
                                    >
                                        {region.cortarName}
                                    </div>
                                ))}
                            </div>

                            {/* 시/군/구 목록 */}
                            {activeRegion && activeRegion !== "" ? (
                                <div className="w-1/2 flex flex-col h-full">
                                    <div className="bg-gray-50 p-2 border-b border-gray-200 flex justify-between items-center">
                                        <h3 className="text-sm font-medium text-gray-700">
                                            시/군/구 선택 ({selectedCities.length}/{MAX_CITY_SELECTION}개)
                                        </h3>
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={handleDeselectAllCities}
                                                className="text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded"
                                            >
                                                해제
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="p-2 bg-blue-50 text-blue-700 text-xs text-center">
                                        검색하려면 최소 1개의 시/군/구를 선택해주세요.
                                    </div>
                                    
                                    <div className="overflow-y-auto flex-grow">
                                        {activeRegionSiGunGuData?.map((city) => (
                                            <div
                                                key={city.cortarNo}
                                                className="p-2 hover:bg-gray-100 text-xs md:text-sm flex items-center"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`city-${city.cortarNo}`}
                                                    checked={selectedCities.some(item => item.cortarNo === city.cortarNo)}
                                                    onChange={() => handleCityCheckboxChange(city)}
                                                    className="mr-2"
                                                    disabled={selectedCities.length >= MAX_CITY_SELECTION && !selectedCities.some(item => item.cortarNo === city.cortarNo)}
                                                />
                                                <label 
                                                    htmlFor={`city-${city.cortarNo}`}
                                                    className={`flex-grow cursor-pointer ${
                                                        selectedCities.length >= MAX_CITY_SELECTION && !selectedCities.some(item => item.cortarNo === city.cortarNo)
                                                        ? 'text-gray-400'
                                                        : ''
                                                    }`}
                                                >
                                                    {city.cortarName}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="w-1/2 flex items-center justify-center text-gray-400 text-sm">
                                    시/도를 먼저 선택해주세요
                                </div>
                            )}
                        </div>
                        
                        {/* 검색하기 버튼 - 전체 드롭다운 하단 */}
                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={handleSearch}
                                disabled={isSearchButtonDisabled}
                                className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors
                                    ${isSearchButtonDisabled 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                            >
                                검색하기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 선택된 시/군/구 표시 (모바일에서는 드롭다운 밖에 표시) */}
            {!isOpen && selectedCities.length > 0 && (
                <div className="mt-2 md:hidden">
                    <div className="flex flex-wrap gap-1 max-w-full">
                        {selectedCities.map((city) => (
                            <span 
                                key={city.cortarNo} 
                                className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs"
                            >
                                {city.parentRegionName} &gt; {city.cortarName}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegionDropdownMetrix; 